import { toast } from '@econsult/econsult-ui/dist/components/Toast';
import { ErrorToast } from 'components/Toast';
import { STRINGS } from 'config';
import React, { Dispatch } from 'react';
import { Pass } from 'store/reducer/wallet';
import request, { HTTPError } from 'superagent';

const {
  SUMMARISER: { HOST },
  REDUCERS: {
    WALLET: {
      GET_WALLET_REQUEST,
      GET_WALLET_SUCCESS,
      GET_WALLET_ERROR,
      GET_PRODUCTS_REQUEST,
      GET_PRODUCTS_SUCCESS,
      GET_PRODUCTS_ERROR,
      DELETE_ORDER_REQUEST,
      DELETE_ORDER_SUCCESS,
      DELETE_ORDER_ERROR,
      ACCEPT_TERMS_REQUEST,
      ACCEPT_TERMS_SUCCESS,
      ACCEPT_TERMS_ERROR,
    },
  },
} = STRINGS;

export const getWallet = async (dispatch: Dispatch<unknown>, selectedPracticeId: string) => {
  dispatch({ type: GET_WALLET_REQUEST });
  await request
    .get(`${HOST}/wallet?practiceId=${selectedPracticeId}`)
    .set('Content-Type', 'application/json')
    .withCredentials()
    .then(({ body }) => {
      const { userId, createdAt, topup, passes, autoCutoffMsRemaining, subscription, termsAccepted } = body;
      return dispatch({
        type: GET_WALLET_SUCCESS,
        payload: {
          userId,
          createdAt,
          topup,
          passes,
          autoCutoffMsRemaining,
          subscription,
          termsAccepted,
        },
      });
    })
    .catch(error => {
      dispatch({ type: GET_WALLET_ERROR, payload: error });
      toast.error(
        React.createElement(ErrorToast, {
          message: 'Could not retrieve wallet',
        }),
        {
          autoClose: 12000,
          pauseOnHover: true,
          hideProgressBar: false,
          closeOnClick: true,
          icon: false,
        },
      );
    });
};

export const getProducts = async (dispatch: Dispatch<unknown>, selectedPracticeId: string) => {
  dispatch({ type: GET_PRODUCTS_REQUEST });
  await request
    .get(`${HOST}/wallet/products?practiceId=${selectedPracticeId}`)
    .set('Content-Type', 'application/json')
    .withCredentials()
    .then(({ body }) =>
      dispatch({
        type: GET_PRODUCTS_SUCCESS,
        payload: body,
      }),
    )
    .catch(error => {
      dispatch({ type: GET_PRODUCTS_ERROR, payload: error });
      toast.error(
        React.createElement(ErrorToast, {
          message: 'Could not retrieve products',
        }),
        {
          autoClose: 12000,
          pauseOnHover: true,
          hideProgressBar: false,
          closeOnClick: true,
          icon: false,
        },
      );
    });
};

export const getOrdersPage = async (page: number, selectedPracticeId: string) => {
  const { body, status, headers } = await request
    .get(`${HOST}/payments/orders?page=${page}&practiceId=${selectedPracticeId}`)
    .set('Content-Type', 'application/json')
    .withCredentials();
  if (status === 200) {
    const hasNext = headers['x-has-next'] === 'true';
    return {
      body,
      hasNext,
    };
  }
  throw new Error(`Failed to fetch page ${page}`);
};

export const activateDayPass = async (
  dispatch: Dispatch<unknown>,
  remainingPasses: Pass[],
  selectedPracticeId: string,
) => {
  try {
    const dayPass = remainingPasses.find((pass: Pass) => pass.label?.toLowerCase().includes('day'));
    if (dayPass === undefined) {
      return null;
    }
    const response = await request
      .post(`${HOST}/wallet/pass/activate?practiceId=${selectedPracticeId}`)
      .set('Content-Type', 'application/json')
      .withCredentials()
      .send({ passId: dayPass.id });
    if (response.status === 200) {
      getWallet(dispatch, selectedPracticeId);
    }
  } catch (error: unknown) {
    const err = error as HTTPError;
    let errorMessage = 'Could not activate pass';
    if ('status' in err) {
      switch (err.status) {
        case 404:
          errorMessage = 'Wallet or pass not found';
          break;
        case 409:
          errorMessage = 'A pass is already active';
          break;
        case 403:
          errorMessage = 'Forbidden';
          break;
        default:
          return errorMessage;
      }
    }
    toast.error(React.createElement(ErrorToast, { message: errorMessage }), {
      autoClose: 12000,
      pauseOnHover: true,
      hideProgressBar: false,
      closeOnClick: true,
      icon: false,
    });
  }
  return undefined;
};

export const activateMonthPass = async (
  dispatch: Dispatch<unknown>,
  remainingPasses: Pass[],
  selectedPracticeId: string,
) => {
  try {
    const monthPass = remainingPasses.find((pass: Pass) => pass.label?.toLowerCase().includes('month'));
    if (monthPass === undefined) {
      return null;
    }
    const response = await request
      .post(`${HOST}/wallet/pass/activate?practiceId=${selectedPracticeId}`)
      .set('Content-Type', 'application/json')
      .withCredentials()
      .send({ passId: monthPass.id });
    if (response.status === 200) {
      getWallet(dispatch, selectedPracticeId);
    }
  } catch (error: unknown) {
    const err = error as HTTPError;
    let errorMessage = 'Could not activate pass';
    if ('status' in err) {
      switch (err.status) {
        case 404:
          errorMessage = 'Wallet or pass not found';
          break;
        case 409:
          errorMessage = 'A pass is already active';
          break;
        case 403:
          errorMessage = 'Forbidden';
          break;
        default:
          return errorMessage;
      }
    }
    toast.error(React.createElement(ErrorToast, { message: errorMessage }), {
      autoClose: 12000,
      pauseOnHover: true,
      hideProgressBar: false,
      closeOnClick: true,
      icon: false,
    });
  }
  return undefined;
};

export const deleteOrder = async (dispatch: Dispatch<unknown>, orderId: string, selectedPracticeId: string) => {
  dispatch({ type: DELETE_ORDER_REQUEST });
  try {
    const response = await request
      .delete(`${HOST}/payments/orders/${orderId}?practiceId=${selectedPracticeId}`)
      .set('Content-Type', 'application/json')
      .withCredentials();
    if (response.status === 204) {
      dispatch({ type: DELETE_ORDER_SUCCESS, payload: { orderId } });
    } else {
      dispatch({ type: DELETE_ORDER_ERROR, payload: response.body });
    }
  } catch (error) {
    dispatch({ type: DELETE_ORDER_ERROR, payload: error });
  }
};

export const acceptTerms = async (dispatch: Dispatch<unknown>, selectedPracticeId: string) => {
  dispatch({ type: ACCEPT_TERMS_REQUEST });
  try {
    const response = await request
      .post(`${HOST}/wallet/accept-terms?practiceId=${selectedPracticeId}`)
      .set('Content-Type', 'application/json')
      .withCredentials();
    if (response.status === 200) {
      dispatch({ type: ACCEPT_TERMS_SUCCESS });
    } else {
      dispatch({ type: ACCEPT_TERMS_ERROR, payload: response.body });
    }
  } catch (error) {
    dispatch({ type: ACCEPT_TERMS_ERROR, payload: error });
    toast.error(
      React.createElement(ErrorToast, {
        message: 'Could not accept terms',
      }),
      {
        autoClose: 12000,
        pauseOnHover: true,
        hideProgressBar: false,
        closeOnClick: true,
        icon: false,
      },
    );
  }
};
